import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Send,
  BookOpen,
  FileText,
  Shield,
  Mail
} from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQ = () => {
  // Search / filters / open-state
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [openKeys, setOpenKeys] = useState(new Set()); // supports multiple open
  const [form, setForm] = useState({ name: '', email: '', topic: 'General', question: '' });
  const [submitted, setSubmitted] = useState(false);

  // FAQ data
  const faqs = [
    {
      id: 'team-1',
      q: 'What makes your team different from other IT service providers?',
      a: 'Our team is composed of former tech professionals and industry specialists with real‑world experience. We don’t just fill roles or complete tasks — we build long‑term partnerships based on transparency, trust, and consistent performance.',
      cat: 'About Our Team'
    },
    {
      id: 'team-2',
      q: 'Do you support both small businesses and large enterprises?',
      a: 'Yes. Our team has experience supporting startups, SMEs, and enterprise‑level environments, including complex infrastructure and multi‑site operations.',
      cat: 'About Our Team'
    },
    {
      id: 'support-1',
      q: 'Do you offer both remote and onsite support?',
      a: 'Yes. We provide remote troubleshooting for quick resolutions and onsite support when hands‑on assistance is required.',
      cat: 'IT Support & Helpdesk'
    },
    {
      id: 'support-2',
      q: 'What types of devices do you support?',
      a: 'We support PCs, laptops, printers, mobile devices, and most standard office hardware.',
      cat: 'IT Support & Helpdesk'
    },
    {
      id: 'support-3',
      q: 'Can you help train our staff?',
      a: 'Yes. We offer end‑user training, including secure software usage, basic troubleshooting, and productivity best practices.',
      cat: 'IT Support & Helpdesk'
    },
    {
      id: 'support-4',
      q: 'Do you handle IT asset management?',
      a: 'Absolutely. We track devices, licenses, warranties, and inventory to help you maintain full visibility and compliance.',
      cat: 'IT Support & Helpdesk'
    },
    {
      id: 'network-1',
      q: 'Can you help set up or upgrade our office network?',
      a: 'Yes. We handle cabling, Wi‑Fi surveys, network device installation, and full LAN/WAN design.',
      cat: 'Network Services'
    },
    {
      id: 'network-2',
      q: 'Do you provide network troubleshooting?',
      a: 'We diagnose and resolve network issues, including connectivity problems, device failures, and performance bottlenecks.',
      cat: 'Network Services'
    },
    {
      id: 'network-3',
      q: 'Can you configure VPNs for remote work?',
      a: 'Yes. We set up secure VPN access so your team can work safely from anywhere.',
      cat: 'Network Services'
    },
    {
      id: 'network-4',
      q: 'Do you manage firewalls?',
      a: 'We configure and maintain firewalls to strengthen your network security posture.',
      cat: 'Network Services'
    },
    {
      id: 'data-1',
      q: 'Do you offer data migration services?',
      a: 'Yes. We migrate data between servers, systems, or cloud environments with minimal downtime.',
      cat: 'Data Center Support'
    },
    {
      id: 'data-2',
      q: 'Can you help with disaster recovery planning?',
      a: 'We design and implement disaster recovery solutions to protect your business from data loss or system failures.',
      cat: 'Data Center Support'
    },
    {
      id: 'data-3',
      q: 'Do you manage databases?',
      a: 'Yes. We support SQL, Oracle, and other database platforms, including maintenance and optimization.',
      cat: 'Data Center Support'
    },
    {
      id: 'infra-1',
      q: 'Do you install and maintain servers?',
      a: 'We provide full server lifecycle support — installation, maintenance, and break‑fix services.',
      cat: 'Infrastructure & Hardware'
    },
    {
      id: 'infra-2',
      q: 'Can you set up office hardware?',
      a: 'Yes. We install desktops, laptops, printers, peripherals, and even ceiling sensors for workplace automation.',
      cat: 'Infrastructure & Hardware'
    },
    {
      id: 'multimedia-1',
      q: 'Do you provide AV support for events?',
      a: 'Yes. We support audio‑video setups, live events, and on‑site production needs.',
      cat: 'Multimedia Services'
    },
    {
      id: 'multimedia-2',
      q: 'Can you integrate multimedia systems into our office?',
      a: 'We design and implement multimedia solutions such as conference room systems, displays, and AV equipment.',
      cat: 'Multimedia Services'
    },
    {
      id: 'general-1',
      q: 'How do we get started?',
      a: 'You can contact us with your requirements, and we’ll schedule a consultation to understand your environment, challenges, and goals.',
      cat: 'General & Engagement Questions'
    },
    {
      id: 'general-2',
      q: 'Do you offer ongoing support contracts?',
      a: 'Yes. We provide flexible support packages, from on‑demand assistance to fully managed services.',
      cat: 'General & Engagement Questions'
    },
    {
      id: 'general-3',
      q: 'Can you customize your services to fit our needs?',
      a: 'Absolutely. Every business is different, and we tailor our solutions to match your workflow, budget, and long‑term plans.',
      cat: 'General & Engagement Questions'
    },
  ];

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(faqs.map(f => f.cat)))],
    [faqs]
  );

  const filteredFaqs = useMemo(() => {
    const qLower = query.trim().toLowerCase();
    return faqs.filter(f => {
      const matchesCat = activeCategory === 'All' || f.cat === activeCategory;
      const matchesQuery =
        !qLower ||
        f.q.toLowerCase().includes(qLower) ||
        f.a.toLowerCase().includes(qLower);
      return matchesCat && matchesQuery;
    });
  }, [faqs, query, activeCategory]);

  const toggleFaq = (id) => {
    setOpenKeys(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Placeholder: integrate Supabase insert if needed
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    setForm({ name: '', email: '', topic: 'General', question: '' });
  };

  return (
    <>
      <Helmet>
        <title>FAQ - Tasks IT Services</title>
        <meta
          name="description"
          content="Frequently asked questions about Tasks IT Services: our IT infrastructure services, delivery process, timelines, support, and how to get started."
        />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden">
        {/* Background */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900" />
        <div className="pointer-events-none absolute -top-40 -left-40 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="pointer-events-none absolute -bottom-40 -right-40 w-[30rem] h-[30rem] bg-blue-600/25 rounded-full blur-3xl" />

        {/* Hero */}
        <section className="relative pt-20 pb-12 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl shadow-xl mb-6">
                  <HelpCircle className="h-10 w-10" />
                </div>
                <h1 className="text-5xl font-bold mb-4">Frequently Asked Questions (FAQ)</h1>
                <p className="text-blue-100 max-w-2xl mx-auto">
                  Answers to common questions about our team, IT support, network services, data center support, hardware, multimedia, and engagement process.
                </p>
              </motion.div>
            </div>
        </section>

        {/* Content */}
        <section className="relative pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
            {/* Left: Filters / Search */}
            <aside className="lg:col-span-4 xl:col-span-3 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-6 shadow-2xl text-white"
              >
                <h3 className="text-lg font-semibold mb-4">Search FAQs</h3>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-blue-200" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search keywords..."
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:border-cyan-400 text-sm text-white placeholder:text-blue-200"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                        activeCategory === cat
                          ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white'
                          : 'bg-white/10 text-blue-100 hover:bg-white/20'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <div className="mt-4 text-xs text-blue-200">
                  {filteredFaqs.length} result{filteredFaqs.length !== 1 && 's'} found
                </div>
              </motion.div>

              {/* Resources (Link instead of href) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-6 shadow-2xl text-white"
              >
                <h3 className="text-lg font-semibold mb-4">Resources</h3>
                <ul className="space-y-3 text-sm">
                  <li>
                    <Link
                      to="/services"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition"
                    >
                      <BookOpen className="h-4 w-4" /> Our Services
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition"
                    >
                      <FileText className="h-4 w-4" /> Contact Us
                    </Link>
                  </li>
                </ul>
              </motion.div>
            </aside>

            {/* Right: FAQ List */}
            <div className="lg:col-span-8 xl:col-span-9 space-y-4">
              <AnimatePresence initial={false}>
                {filteredFaqs.map((f, i) => {
                  const open = openKeys.has(f.id);
                  return (
                    <motion.div
                      key={f.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.25, delay: i * 0.02 }}
                      className="group relative"
                    >
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-600/0 via-cyan-500/0 to-purple-500/0 blur-xl opacity-0 group-hover:opacity-40 transition" />
                      <div className="relative backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl overflow-hidden text-white">
                        <button
                          onClick={() => toggleFaq(f.id)}
                          className="w-full flex items-center justify-between text-left px-5 py-4"
                        >
                          <span className="font-semibold">{f.q}</span>
                          {open ? <ChevronUp className="h-5 w-5 text-blue-200" /> : <ChevronDown className="h-5 w-5 text-blue-200" />}
                        </button>
                        <AnimatePresence initial={false}>
                          {open && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="px-5 pb-5 text-sm text-blue-100 leading-relaxed space-y-3"
                            >
                              <p>{f.a}</p>
                              {f.id === 'technical-1' && (
                                <p>
                                  See our full list of services and deliverables on the{' '}
                                  <Link
                                    to="/services"
                                    className="text-cyan-300 underline hover:text-cyan-200"
                                  >
                                    Services page
                                  </Link>.
                                </p>
                              )}
                              {f.id === 'privacy-1' && (
                                <p>
                                  Full details: <Link to="/privacy" className="text-cyan-300 underline hover:text-cyan-200">Privacy Policy</Link>.
                                </p>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {filteredFaqs.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center text-blue-100 py-16 backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl"
                >
                  <p className="text-lg font-semibold mb-2">No matching FAQs</p>
                  <p className="text-sm mb-4">Try different keywords or submit a new question.</p>
                  <button
                    onClick={() => { setQuery(''); setActiveCategory('All'); }}
                    className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-sm font-medium"
                  >
                    Reset Filters
                  </button>
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center text-blue-200 text-xs pt-6"
              >
                © {new Date().getFullYear()} Tasks IT Services. More information on the{' '}
                <Link to="/services" className="text-cyan-300 underline hover:text-cyan-200">
                  Services page
                </Link>.
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQ;